import React, { useContext, useState } from 'react';
import { useAuthQuery as useQuery } from '../../../../shared/hooks/useAuthQuery';
import { Button } from '../../../../catalyst-components/button';
import { useNavigate } from 'react-router-dom';
import { useMutation } from 'convex/react';
import { api } from '../../../../convex/_generated/api';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../../catalyst-components/table';
import { Id } from '../../../../convex/_generated/dataModel';
import { BaseContext } from '../../../../shared/base-context';
import { Switch } from '../../../../catalyst-components/switch';
import { Alert, AlertActions, AlertDescription, AlertTitle } from '../../../../catalyst-components/alert';
import {
  ArrowLeftIcon,
  PlusIcon,
  PencilIcon,
  SwatchIcon,
  HandThumbUpIcon,
  HandThumbDownIcon,
} from '@heroicons/react/24/outline';

const CATEGORIES = [
  { value: 'session', label: 'Session Chat' },
  { value: 'evergreen', label: 'Evergreen Chat' },
  { value: 'both', label: 'Both' },
  { value: 'system', label: 'System' },
];

export const Tools: React.FC = () => {
  const navigate = useNavigate();
  const { convexUserId } = useContext(BaseContext);
  const tools = useQuery(api.ai_tools.list);
  const feedbackStats = useQuery(api.widget_feedback.getAllStats);
  const toggleEnabled = useMutation(api.ai_tools.toggleEnabled);
  const seedTools = useMutation(api.migrations.seed_ai_tools.seedFromAdmin);
  const backfillWidgets = useMutation(api.migrations.seed_ai_tools.backfillWidgetFlags);

  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [filterCategory, setFilterCategory] = useState<string>('all');

  const showNotification = (message: string) => {
    setAlertMessage(message);
    setShowAlert(true);
  };

  const handleToggle = async (toolId: Id<"ai_tools">) => {
    if (!convexUserId) return;
    try {
      await toggleEnabled({ id: toolId, updated_by: convexUserId });
    } catch (error: any) {
      showNotification(`Error: ${error.message}`);
    }
  };

  const handleSeed = async () => {
    if (!convexUserId) return;
    try {
      const result = await seedTools({ userId: convexUserId });
      showNotification(`Seeded ${result.seeded} of ${result.total} tools`);
    } catch (error: any) {
      showNotification(`Error: ${error.message}`);
    }
  };

  const handleBackfillWidgets = async () => {
    if (!convexUserId) return;
    try {
      const result = await backfillWidgets({ userId: convexUserId });
      showNotification(`Updated widget flags on ${result.updated} of ${result.total} tools`);
    } catch (error: any) {
      showNotification(`Error: ${error.message}`);
    }
  };

  const filteredTools = tools?.filter(
    (t) => filterCategory === 'all' || t.category === filterCategory
  );

  const getCategoryBadge = (category: string) => {
    const colors: Record<string, string> = {
      session: 'bg-blue-100 text-blue-800',
      evergreen: 'bg-green-100 text-green-800',
      both: 'bg-purple-100 text-purple-800',
      system: 'bg-gray-100 text-gray-800',
    };
    return (
      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${colors[category] || colors.system}`}>
        {CATEGORIES.find((c) => c.value === category)?.label || category}
      </span>
    );
  };

  return (
    <div className="min-h-screen bg-background pt-24">
      {/* Header */}
      <div className="border-b border-border/50 bg-gradient-to-r from-primary/5 to-accent/5 px-6 py-8">
        <div>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button plain onClick={() => navigate('/admin')}>
                <ArrowLeftIcon className="h-5 w-5" />
              </Button>
              <div>
                <h1 className="text-3xl font-semibold text-foreground mb-1">AI Tools</h1>
                <p className="text-base text-muted-foreground">
                  Manage OpenAI function calling tool definitions used by the AI assistants
                </p>
              </div>
            </div>
            <Button
              color="dark"
              onClick={() => navigate('new')}
            >
              <PlusIcon className="h-5 w-5 mr-1" />
              New Tool
            </Button>
          </div>
        </div>
      </div>

      <div className="px-6 py-8">
        <div className="space-y-8">
          {/* Backfill banner — shown when tools exist but some are missing widget flags */}
          {tools && tools.length > 0 && tools.some((t) => t.produces_widget === undefined) && (
            <div className="flex items-center justify-between rounded-lg border border-violet-200 bg-violet-50 px-4 py-3">
              <div className="flex items-center gap-2">
                <SwatchIcon className="h-4 w-4 text-violet-600" />
                <span className="text-sm text-violet-800">
                  Some tools are missing widget flags. Run backfill to update them.
                </span>
              </div>
              <Button color="dark" onClick={handleBackfillWidgets}>
                Backfill Widget Flags
              </Button>
            </div>
          )}

          {/* Filter */}
          <div className="flex items-center gap-3">
            <span className="text-sm text-muted-foreground">Filter:</span>
            <div className="flex gap-2">
              {[{ value: 'all', label: 'All' }, ...CATEGORIES].map((cat) => (
                <button
                  key={cat.value}
                  onClick={() => setFilterCategory(cat.value)}
                  className={`px-3 py-1 rounded-full text-sm transition-colors ${
                    filterCategory === cat.value
                      ? 'bg-primary text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {cat.label}
                </button>
              ))}
            </div>
            <span className="text-sm text-muted-foreground ml-auto">
              {filteredTools?.length ?? 0} tool{filteredTools?.length !== 1 ? 's' : ''}
            </span>
          </div>

          {/* Tools Table — fixed layout on wide viewports, horizontal scroll on narrow */}
          <div className="border rounded-xl overflow-hidden bg-white">
            <style>{`
              .tools-table-wrap > div > div { white-space: normal !important; }
              .tools-table-wrap table { table-layout: fixed; width: 100%; min-width: 1100px; }
              .tools-table-wrap td { overflow: hidden; }
            `}</style>
            <div className="tools-table-wrap">
              <Table dense>
                <TableHead>
                  <TableRow>
                    <TableHeader className="w-[12%]">Name</TableHeader>
                    <TableHeader className="w-[20%]">Description</TableHeader>
                    <TableHeader className="w-[12%]">Parameters</TableHeader>
                    <TableHeader className="w-[8%]">Category</TableHeader>
                    <TableHeader className="w-[13%]">Widget</TableHeader>
                    <TableHeader className="w-[10%]">Feedback</TableHeader>
                    <TableHeader className="w-[7%]">Usage</TableHeader>
                    <TableHeader className="w-[8%]">Enabled</TableHeader>
                    <TableHeader className="w-[10%]">Actions</TableHeader>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredTools?.map((tool) => (
                    <TableRow key={tool._id}>
                      <TableCell>
                        <div>
                          <span className="font-medium text-foreground text-sm">{tool.display_name}</span>
                          <br />
                          <code className="text-xs text-muted-foreground bg-gray-50 px-1 py-0.5 rounded break-all">
                            {tool.name}
                          </code>
                        </div>
                      </TableCell>
                      <TableCell>
                        <p className="text-sm text-muted-foreground line-clamp-2" title={tool.description}>
                          {tool.description}
                        </p>
                      </TableCell>
                      <TableCell>
                        {(() => {
                          try {
                            const params = JSON.parse(tool.parameters);
                            const props = params.properties || {};
                            const required = params.required || [];
                            const propNames = Object.keys(props);
                            if (propNames.length === 0) {
                              return <span className="text-xs text-muted-foreground italic">None</span>;
                            }
                            return (
                              <div className="flex flex-wrap gap-1">
                                {propNames.map((name) => (
                                  <span
                                    key={name}
                                    className={`px-1.5 py-0.5 rounded text-xs font-mono ${
                                      required.includes(name)
                                        ? 'bg-amber-50 text-amber-800 border border-amber-200'
                                        : 'bg-gray-50 text-gray-600 border border-gray-200'
                                    }`}
                                    title={`${name}${props[name]?.type ? ': ' + props[name].type : ''}${required.includes(name) ? ' (required)' : ' (optional)'}`}
                                  >
                                    {name}{required.includes(name) ? '*' : ''}
                                  </span>
                                ))}
                              </div>
                            );
                          } catch {
                            return <span className="text-xs text-red-500">Invalid JSON</span>;
                          }
                        })()}
                      </TableCell>
                      <TableCell>{getCategoryBadge(tool.category)}</TableCell>
                      <TableCell>
                        {tool.produces_widget ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-violet-100 text-violet-800 max-w-full overflow-hidden">
                            <SwatchIcon className="h-3 w-3 shrink-0" />
                            <span className="truncate">{tool.widget_type || 'yes'}</span>
                          </span>
                        ) : (
                          <span className="text-xs text-muted-foreground">—</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {tool.produces_widget && feedbackStats?.[tool.name] ? (
                          <div className="flex items-center gap-2 text-xs">
                            <span className="inline-flex items-center gap-0.5 text-emerald-700">
                              <HandThumbUpIcon className="h-3 w-3" />
                              {feedbackStats[tool.name].positive}
                            </span>
                            <span className="inline-flex items-center gap-0.5 text-red-500">
                              <HandThumbDownIcon className="h-3 w-3" />
                              {feedbackStats[tool.name].negative}
                            </span>
                          </div>
                        ) : (
                          <span className="text-xs text-muted-foreground">—</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="text-sm">
                          <span className="font-medium text-foreground">{tool.usage_count || 0}</span>
                          {tool.last_used_at && (
                            <p className="text-xs text-muted-foreground mt-0.5">
                              {new Date(tool.last_used_at).toLocaleDateString('en-US', {
                                month: 'short',
                                day: 'numeric',
                              })}
                            </p>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Switch
                          checked={tool.is_enabled}
                          onClick={() => handleToggle(tool._id)}
                          color="emerald"
                        />
                      </TableCell>
                      <TableCell>
                        <Button plain onClick={() => navigate(tool._id)}>
                          <PencilIcon className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  {filteredTools?.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={9}>
                        <div className="text-center py-8 text-muted-foreground">
                          <p className="mb-3">No tools found.</p>
                          {tools?.length === 0 && (
                            <Button color="dark" onClick={handleSeed}>
                              Seed Default Tools
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </div>
        </div>
      </div>

      {/* Notifications */}
      <Alert open={showAlert} onClose={() => setShowAlert(false)}>
        <AlertTitle>Notification</AlertTitle>
        <AlertDescription>{alertMessage}</AlertDescription>
        <AlertActions>
          <Button onClick={() => setShowAlert(false)}>OK</Button>
        </AlertActions>
      </Alert>

    </div>
  );
};
